// Form controls styled like the iOS fields (tonal rounded boxes, Theme.Radius.sm). `Field` wraps one control with a
// label, hint and error and wires the ids, so screens never hand-roll aria-describedby: Input, TextArea and Select
// pick the id, description and invalid state up from the surrounding Field.

import { ChevronDown } from 'lucide-react';
import {
  createContext,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** The surrounding Field's wiring (for custom controls that want the same label/description ids). */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  /** Help text under the control. */
  hint?: ReactNode;
  /** Error text; also marks the control aria-invalid. */
  error?: ReactNode;
  required?: boolean;
  /** Something on the label's line, right-aligned (a "Forgot password?" link, a character count). */
  trailing?: ReactNode;
  /** Use a given id for the control instead of a generated one. */
  id?: string;
  /** Visually hide the label (still read by screen readers), e.g. a search box in a toolbar. */
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required = false, trailing, id, hideLabel, className, children }: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `f${autoId}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <FieldContext.Provider value={{ id: controlId, describedBy, invalid: !!error, required }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div className={cn('flex items-baseline justify-between gap-2', hideLabel && 'sr-only')}>
          <label htmlFor={controlId} className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
            {label}
            {required && (
              <span aria-hidden className="ml-0.5 text-danger-text">
                *
              </span>
            )}
          </label>
          {trailing}
        </div>
        {children}
        {error ? (
          <p id={errorId} className="text-xs font-medium text-danger-text">
            {error}
          </p>
        ) : null}
        {hint ? (
          <p id={hintId} className="text-xs text-fg-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/** Shared look of text-like controls; exported for custom inputs (code boxes, color fields). */
export const controlClass =
  'w-full min-w-0 rounded-control border border-line-strong bg-surface text-fg shadow-[inset_0_1px_1px_rgb(16_42_94/0.03)] ' +
  'transition-[border-color,box-shadow,background-color] duration-150 outline-none ' +
  'placeholder:text-fg-subtle hover:border-[color-mix(in_srgb,var(--gt-fg)_28%,transparent)] ' +
  'focus:border-primary focus:ring-3 focus:ring-primary/20 focus-visible:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:opacity-70 ' +
  'aria-invalid:border-danger aria-invalid:focus:ring-danger/20';

/** Text inputs only: `:read-only` also matches every <select>, so it cannot live in controlClass. */
const READ_ONLY_CLASS = 'read-only:bg-surface-2 read-only:focus:border-line-strong read-only:focus:ring-0';

const CONTROL_SIZE = {
  sm: 'h-8 px-2.5 text-[13px]',
  md: 'h-10 px-3 text-sm',
  lg: 'h-12 px-4 text-base',
} as const;

export type ControlSize = keyof typeof CONTROL_SIZE;

function useControlWiring(props: {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: InputHTMLAttributes<HTMLInputElement>['aria-invalid'];
  required?: boolean;
  invalid?: boolean;
}) {
  const field = useContext(FieldContext);
  return {
    id: props.id ?? field?.id,
    'aria-describedby': [props['aria-describedby'], field?.describedBy].filter(Boolean).join(' ') || undefined,
    'aria-invalid': props.invalid || field?.invalid || props['aria-invalid'] ? true : undefined,
    required: props.required ?? (field?.required || undefined),
  };
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Icon inside the left edge (search glass, currency sign). */
  leadingIcon?: ReactNode;
  /** Content inside the right edge (unit label, clear button, reveal toggle). */
  trailing?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function Input({ size = 'md', invalid, leadingIcon, trailing, className, ref, ...rest }: InputProps) {
  const wiring = useControlWiring({ ...rest, invalid });
  const input = (
    <input
      ref={ref}
      {...rest}
      {...wiring}
      className={cn(
        controlClass,
        READ_ONLY_CLASS,
        CONTROL_SIZE[size],
        leadingIcon != null && (size === 'sm' ? 'pl-8' : 'pl-9'),
        trailing != null && 'pr-10',
        leadingIcon == null && trailing == null && className,
      )}
    />
  );
  if (leadingIcon == null && trailing == null) return input;
  return (
    <div className={cn('relative flex items-center', className)}>
      {leadingIcon != null && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 flex items-center text-fg-subtle [&_svg]:size-4',
            size === 'sm' ? 'pl-2.5' : 'pl-3',
          )}
        >
          {leadingIcon}
        </span>
      )}
      {input}
      {trailing != null && <span className="absolute inset-y-0 right-1 flex items-center">{trailing}</span>}
    </div>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Grow with the content up to this many rows (CSS field-sizing where supported; else fixed `rows`). */
  autoGrow?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextArea({ invalid, autoGrow, rows = 4, className, ref, ...rest }: TextAreaProps) {
  const wiring = useControlWiring({ ...rest, invalid });
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...rest}
      {...wiring}
      className={cn(
        controlClass,
        READ_ONLY_CLASS,
        'min-h-20 resize-y px-3 py-2 text-sm leading-relaxed',
        autoGrow && '[field-sizing:content] max-h-80',
        className,
      )}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Convenience: render these options (children are still rendered after them, e.g. <optgroup>s). */
  options?: readonly SelectOption[];
  /** A first, empty option ("Choose…"); selecting it yields value "". */
  placeholder?: string;
  ref?: Ref<HTMLSelectElement>;
}

/** Native <select> (the platform picker is the most accessible one) with the field look and a chevron. */
export function Select({ size = 'md', invalid, options, placeholder, className, children, ref, ...rest }: SelectProps) {
  const wiring = useControlWiring({ ...rest, invalid });
  return (
    <div className={cn('relative', className)}>
      <select
        ref={ref}
        {...rest}
        {...wiring}
        className={cn(controlClass, CONTROL_SIZE[size], 'cursor-pointer appearance-none pr-9')}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-subtle"
      />
    </div>
  );
}
